/**
 * ADMIN-ZONE-SYNC01: canonical source of truth зон — detection_configs["zones"]
 * через Zones API. Generic camera PATCH не имеет права перезаписывать его
 * stale-копией из формы, а monitoring_zone — compatibility projection, которую
 * backend строит сам. Оба ключа из payload формы вырезаются.
 */
const ZONE_KEYS = ['zones', 'monitoring_zone'];

/**
 * ТЗ №33 (ответ backend 2026-10-05): включение детекторов меняется только через
 * PATCH /admin/cameras/{id}/detection-profile. Форма камеры эти ключи не
 * показывает и не меняет — в payload уходят значения, которые пришли с камеры.
 * Реальные ключи в данных — masks_detector (в ТЗ опечатка mask_detector).
 */
export const DETECTOR_CONFIG_KEYS = ['gloves_detector', 'masks_detector', 'hats_detector', 'people_detector'];

const parseObject = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const omitKeys = (object, keys) => {
  const result = { ...object };
  keys.forEach((key) => { delete result[key]; });
  return result;
};

export const stripZoneKeysFromDetectionConfigs = (jsonString) => {
  const parsed = parseObject(jsonString);
  if (!parsed || typeof jsonString !== 'string') return jsonString;
  return JSON.stringify(omitKeys(parsed, ZONE_KEYS));
};

/**
 * Без зон и детекторов — их меняют отдельные редакторы. Принимает строку или
 * объект (backend может отдать и то и другое) и возвращает строку;
 * нечитаемая строка возвращается как есть.
 */
export const stripDetectorKeysFromDetectionConfigs = (value) => {
  const parsed = parseObject(value);
  if (!parsed) return typeof value === 'string' ? value : '';
  return JSON.stringify(omitKeys(parsed, [...ZONE_KEYS, ...DETECTOR_CONFIG_KEYS]));
};

/**
 * Текст поля «Конфигурация детекции» в форме камеры: конфигурация камеры
 * (строка или объект) без зон и детекторов. Если её нет или она нечитаема —
 * шаблон типа камеры, тоже без зон и детекторов: иначе детекторы шаблона
 * показывались бы в поле, а при сохранении молча заменялись бы детекторами камеры.
 */
export const detectionConfigsFormText = (value, fallbackConfig = {}) => {
  const parsed = parseObject(value) || parseObject(fallbackConfig) || {};
  return JSON.stringify(omitKeys(parsed, [...ZONE_KEYS, ...DETECTOR_CONFIG_KEYS]), null, 2);
};

/**
 * detection_configs для PATCH камеры — строка (тем же типом, что приходит с
 * backend). Из формы берётся всё, кроме зон и детекторов; детекторы — ровно
 * те, что были у камеры до редактирования. Нечитаемый JSON формы
 * отправляется как есть (его заранее отсекает валидация).
 */
export const buildCameraDetectionConfigsPayload = (formJson, originalConfigs) => {
  const form = parseObject(formJson);
  if (!form) return formJson;

  const result = omitKeys(form, [...ZONE_KEYS, ...DETECTOR_CONFIG_KEYS]);
  const original = parseObject(originalConfigs);
  if (original) {
    DETECTOR_CONFIG_KEYS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(original, key)) result[key] = original[key];
    });
  }
  return JSON.stringify(result);
};
